import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, Download, Sparkles, Award, Rocket,
  Cloud, Activity,
} from "lucide-react";
import TypingText from "../components/TypingText";
import SectionHeader from "../components/SectionHeader";
import { personal, projects, skills, highlights } from "../data/mock";

const useReveal = () => {
  useEffect(() => {
    const els = document.querySelectorAll(".reveal");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
};

const highlightIcons = [Rocket, Activity, Award, Cloud];

const Home = () => {
  useReveal();
  const allSkills = Object.values(skills).flat().map((s) => s.name);
  const marquee = [...allSkills, ...allSkills];
  const heroRef = useRef(null);

  return (
    <div>
      {/* ================= HERO ================= */}
      <section
        ref={heroRef}
        className="relative overflow-hidden border-b hairline"
      >
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="noise" />

        <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-16 md:pb-24">
          {/* Top meta row */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-10 md:mb-14 font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
            <div className="flex items-center  gap-3">
              <span className="inline-flex items-center gap-2 text-green-500">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />
                {personal.status}
              </span>
              <span className="h-px w-10 bg-foreground/40" />
              <span>{personal.location.split("—")[0].trim()}</span>
            </div>
            <div className="hidden md:flex items-center gap-6">
              <span>Portfolio — 2026</span>
              <span></span>
            </div>
          </div>

          {/* Main display + portrait */}
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end">
            {/* LEFT — type */}
            <div className="col-span-12 lg:col-span-8 order-2 lg:order-1">
              {/* Current-role pill */}
              <div className="inline-flex items-center gap-3 px-3 py-1.5 rounded-full border hairline bg-background/60 backdrop-blur mb-6 md:mb-8">
                <span className="w-1.5 h-1.5 rounded-full bg-foreground animate-pulse" />
                <span className="font-mono text-[11px] uppercase tracking-widest">
                  {personal.role}
                </span>
                <span className="h-3 w-px bg-border" />
                <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
                  {personal.company} · since {personal.since}
                </span>
              </div>

              <h1 className="display-xl text-[16vw] md:text-[13vw] lg:text-[10.5vw] leading-[0.9] uppercase">
                Harsh
                <br />
                <span className="italic font-light normal-case lowercase">tripathi.</span>
              </h1>

              {/* Tagline */}
              <div className="mt-8 md:mt-10 flex flex-wrap items-baseline gap-3 md:gap-5">
                <span className="font-mono text-xs text-muted-foreground uppercase tracking-widest">
                  I am a
                </span>
                <span className="font-display text-2xl md:text-4xl tracking-tight">
                  <TypingText words={personal.taglines} />
                </span>
              </div>

              {/* CTA */}
              <div className="mt-8 md:mt-10 flex flex-wrap items-center gap-4">
                <Link to="/work" className="btn-primary" data-cursor-hover>
                  View selected work <ArrowUpRight size={16} />
                </Link>
                <Link to="/contact" className="btn-ghost" data-cursor-hover>
                  Get in touch <ArrowRight size={16} />
                </Link>
                <a
                  href={personal.resumeUrl} download
                  className="text-sm font-medium link-grow ml-1 inline-flex items-center gap-2"
                  data-cursor-hover
                >
                  <Download size={14} /> Resume
                </a>
              </div>
            </div>

            {/* RIGHT — portrait */}
            <div className="col-span-12 lg:col-span-4 order-1 lg:order-2">
              <div className="relative w-full max-w-[360px] lg:max-w-none mx-auto">
                <div className="relative aspect-[4/5] w-full overflow-hidden rounded-md border hairline bg-muted/40 group">
                  <img
                    src={personal.photoUrl}
                    alt="Harsh Tripathi"
                    loading="eager"
                    className="w-full h-full object-cover grayscale contrast-[1.02] group-hover:grayscale-0 transition-[filter,transform] duration-700 ease-[cubic-bezier(.2,.7,.2,1)] group-hover:scale-[1.02]"
                  />
                  {/* Overlay tag */}
                  <div className="absolute left-3 bottom-3 right-3 flex items-center justify-between gap-2">
                    <div className="px-2.5 py-1 rounded-full bg-background/80 backdrop-blur border hairline font-mono text-[10px] uppercase tracking-widest">
                      2026
                    </div>
                    <div className="px-2.5 py-1 rounded-full bg-foreground text-background font-mono text-[10px] uppercase tracking-widest">
                      Lead Engineer
                    </div>
                  </div>
                </div>
                {/* Corner ticks */}
                <span className="absolute -top-1 -left-1 w-4 h-4 border-t border-l hairline" />
                <span className="absolute -top-1 -right-1 w-4 h-4 border-t border-r hairline" />
                <span className="absolute -bottom-1 -left-1 w-4 h-4 border-b border-l hairline" />
                <span className="absolute -bottom-1 -right-1 w-4 h-4 border-b border-r hairline" />
              </div>
            </div>
          </div>
        </div>

        {/* Highlights strip (big, prominent) */}
        <div className="relative border-t hairline">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-2 md:grid-cols-4">
            {highlights.map((h, i) => {
              const Icon = highlightIcons[i % highlightIcons.length];
              return (
                <div
                  key={h.label}
                  className={`py-7 md:py-10 px-1 md:px-4 ${
                    i !== highlights.length - 1 ? "md:border-r hairline" : ""
                  } ${i % 2 === 0 ? "border-r md:border-r hairline" : ""} ${
                    i < 2 ? "border-b md:border-b-0 hairline" : ""
                  }`}
                >
                  <div className="flex items-center gap-2 font-mono text-[10px] text-muted-foreground uppercase tracking-widest">
                    <Icon size={13} />
                    {h.label}
                  </div>
                  <div className="font-display text-2xl md:text-4xl mt-3 leading-none tracking-tight">
                    {h.value}
                  </div>
                  <div className="mt-2 text-xs md:text-sm text-muted-foreground">
                    {h.meta}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ================= MARQUEE ================= */}
      <section className="border-b hairline overflow-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-6">
          {marquee.map((s, i) => (
            <span
              key={i}
              className="font-display text-3xl md:text-5xl mx-6 md:mx-10 text-foreground/70 hover:text-foreground transition-colors"
            >
              {s} <span className="text-muted-foreground mx-2">—</span>
            </span>
          ))}
        </div>
      </section>

      {/* ================= INTRO ================= */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36 grid grid-cols-12 gap-8 reveal">
        <div className="col-span-12 md:col-span-4">
          <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
            — 00 / Intro
          </div>
        </div>
        <div className="col-span-12 md:col-span-8">
          <p className="font-display text-2xl md:text-4xl leading-[1.2] tracking-tight">
            I'm a backend-focused software engineer based in India,
            currently <span className="underline decoration-1 underline-offset-4">Lead Engineer at HCLTech</span>, where I build and scale
            microservices that handle over a <span className="italic">million</span> masked financial
            transactions every day.
          </p>
          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-2">
                Focus
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Node.js, Java / Spring Boot, PostgreSQL, MongoDB, Redis — and the AWS services around them.
              </p>
            </div>
            <div>
              <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-2">
                Currently
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Leading backend engineering at HCLTech since Aug 2025, while pursuing an MCA in Artificial Intelligence at Amity University.
              </p>
            </div>
            <div>
              <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-2">
                Available
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Full-time roles, consulting and collaborations on distributed systems and cloud-native products.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= SELECTED WORK ================= */}
      <section className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 pt-20 md:pt-28 pb-10">
          <SectionHeader
            index="04"
            eyebrow="Selected work"
            title={<>Systems built to stay quiet under load.</>}
            kicker="A small, honest selection of what I've shipped recently."
          />
        </div>

        <div className="border-t hairline">
          {projects.slice(0, 3).map((p, i) => (
            <Link
              to="/work"
              key={p.id}
              className="group block border-b hairline reveal"
              data-cursor-hover
            >
              <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-10 md:py-14 grid grid-cols-12 gap-6 items-center">
                <div className="col-span-12 md:col-span-1 font-mono text-xs text-muted-foreground">
                  0{i + 1}
                </div>
                <div className="col-span-12 md:col-span-5">
                  <h3 className="font-display text-3xl md:text-5xl leading-none group-hover:translate-x-2 transition-transform">
                    {p.title}
                  </h3>
                  <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mt-3">
                    {p.client} · {p.year}
                  </div>
                </div>
                <div className="col-span-12 md:col-span-4">
                  <p className="text-sm md:text-base text-muted-foreground leading-relaxed">
                    {p.summary}
                  </p>
                </div>
                <div className="col-span-12 md:col-span-2 flex md:justify-end">
                  <div className="w-12 h-12 rounded-full border hairline grid place-items-center group-hover:bg-foreground group-hover:text-background transition-colors">
                    <ArrowUpRight size={18} />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-12">
          <Link to="/work" className="btn-ghost" data-cursor-hover>
            See full portfolio <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ================= PHILOSOPHY ================= */}
      <section className="border-t hairline bg-muted/30">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 grid grid-cols-12 gap-8">
          <div className="col-span-12 md:col-span-4">
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest">
              — Philosophy
            </div>
            <h3 className="display-xl text-4xl md:text-5xl mt-4">
              Boring is a<br />
              <span className="italic font-light">feature.</span>
            </h3>
          </div>
          <div className="col-span-12 md:col-span-8 space-y-10">
            {[
              {
                k: "01",
                t: "Reliability before cleverness.",
                d: "Predictable services beat clever architectures that page you at 3am. Observability, idempotency and clear contracts first.",
              },
              {
                k: "02",
                t: "Performance is a design decision.",
                d: "Caching, async work and careful query design aren't afterthoughts. They're baked into the data model.",
              },
              {
                k: "03",
                t: "Write code a calm team can maintain.",
                d: "Small services, strong typing where it earns its keep, and documentation that keeps onboarding under an afternoon.",
              },
            ].map((item) => (
              <div
                key={item.k}
                className="grid grid-cols-12 gap-4 border-t hairline pt-8 group"
                data-cursor-hover
              >
                <div className="col-span-2 md:col-span-1 font-mono text-xs text-muted-foreground">
                  {item.k}
                </div>
                <div className="col-span-10 md:col-span-11">
                  <h4 className="font-display text-2xl md:text-3xl tracking-tight">
                    {item.t}
                  </h4>
                  <p className="mt-3 text-muted-foreground max-w-2xl leading-relaxed">
                    {item.d}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ================= CTA ================= */}
      <section className="border-t hairline">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-32 flex flex-col md:flex-row md:items-end md:justify-between gap-10">
          <div>
            <div className="font-mono text-[11px] text-muted-foreground uppercase tracking-widest mb-4 inline-flex items-center gap-2">
              <Sparkles size={12} /> Currently taking on work
            </div>
            <h3 className="display-xl text-5xl md:text-7xl">
              Have a system that<br />
              needs to <span className="italic font-light">scale?</span>
            </h3>
          </div>
          <Link to="/contact" className="btn-primary" data-cursor-hover>
            Start a project <ArrowUpRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;
